import { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Save } from 'lucide-react';
import { TimelineItem, FunctionType } from '@/types/item';
import { useUpdateGateData } from '@/hooks/useItems';
import { toast } from 'sonner';

interface ItemsFunctionDialogProps {
  item: TimelineItem | null;
  functionType: FunctionType | null;
  isOpen: boolean;
  onClose: () => void;
}

const functionColors = {
  shop: 'hsl(var(--primary))',
  specs: 'hsl(var(--primary))',
  po: 'hsl(var(--primary))',
  contract: 'hsl(var(--primary))'
};

const functionLabels = {
  shop: 'Shop',
  specs: 'Specs', 
  po: 'PO',
  contract: 'Contract'
};

const gateLabels = {
  g1: 'G1-Intake',
  g2: 'G2-Proposal',
  g3: 'G3-For Cons',
  g4: 'G4-Defect + VO',
  g5: 'G5-Archive'
};

const gateColors = {
  g1: 'bg-blue-500',
  g2: 'bg-green-500',
  g3: 'bg-yellow-500',
  g4: 'bg-orange-500',
  g5: 'bg-purple-500'
};

const ItemsFunctionDialog = ({ item, functionType, isOpen, onClose }: ItemsFunctionDialogProps) => {
  const [localData, setLocalData] = useState<Record<string, number>>({});
  const updateGateData = useUpdateGateData();

  useEffect(() => {
    if (item && functionType) {
      // TimelineItem uses timelineData instead of data
      const functionData = item.timelineData?.[functionType];
      if (functionData && typeof functionData === 'object') {
        // Extract percentages from gate data for editing
        const gatePercentages: Record<string, number> = {};
        ['g1', 'g2', 'g3', 'g4', 'g5'].forEach(gate => {
          const gateData = functionData[gate as keyof typeof functionData];
          gatePercentages[gate] = (gateData && typeof gateData === 'object' && 'percentage' in gateData) 
            ? (gateData.percentage as number) || 0 
            : 0;
        });
        setLocalData(gatePercentages);
      } else {
        // Fallback to empty data
        setLocalData({ g1: 0, g2: 0, g3: 0, g4: 0, g5: 0 });
      }
    }
  }, [item, functionType]);

  if (!item || !functionType) return null;

  const updateGateValue = (gate: 'g1' | 'g2' | 'g3' | 'g4' | 'g5', value: number) => {
    const currentValues = { ...localData };
    currentValues[gate] = value;
    
    // Auto-balance to ensure total = 100%
    const total = Object.values(currentValues).reduce((sum, val) => sum + (val || 0), 0);
    if (total !== 100 && total > 0) {
      const otherGates = (['g1', 'g2', 'g3', 'g4', 'g5'] as const).filter(g => g !== gate);
      const remaining = 100 - value;
      const otherTotal = otherGates.reduce((sum, g) => sum + (currentValues[g] || 0), 0);
      
      if (otherTotal > 0) {
        otherGates.forEach(g => {
          currentValues[g] = Math.round((currentValues[g] / otherTotal) * remaining);
        });
      }
    }

    setLocalData(currentValues);
  };

  const handleSave = async () => {
    if (!item || !functionType) return;
    
    try {
      // Save each gate's data to the backend
      const gatePromises = Object.entries(localData).map(([gate, percentage]) => 
        updateGateData.mutateAsync({
          item_id: item.id,
          function_type: functionType,
          gate_name: gate,
          percentage
        })
      );
      
      await Promise.all(gatePromises);
      toast.success('Đã lưu dữ liệu thành công');
      onClose();
    } catch (error) {
      logger.error('[ItemsFunctionDialog] Failed to save gate data:', error);
      toast.error('Không thể lưu dữ liệu. Vui lòng thử lại.');
    }
  };

  const total = Object.values(localData).reduce((sum, val) => sum + (val || 0), 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{item.name}</span>
            <Badge variant="outline">{functionLabels[functionType]}</Badge>
            <Badge 
              variant="outline" 
              className="text-sm"
              style={{ color: functionColors[functionType] }}
            >
              Total: {total}%
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            {(['g1', 'g2', 'g3', 'g4', 'g5'] as const).map((gate) => (
              <div key={gate} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-sm ${gateColors[gate]}`}></div>
                    <label className="text-sm font-medium">{gateLabels[gate]}</label>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {localData[gate] || 0}%
                  </span>
                </div>
                <Slider
                  value={[localData[gate] || 0]}
                  onValueChange={(value) => updateGateValue(gate, value[0])}
                  max={100}
                  step={5}
                  className="w-full"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Hủy
            </Button>
            <Button 
              onClick={handleSave} 
              className="flex items-center gap-2"
              disabled={updateGateData.isPending}
            >
              <Save className="h-4 w-4" />
              {updateGateData.isPending ? 'Đang lưu...' : 'Lưu thay đổi'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ItemsFunctionDialog;